import sdkAccountService, {
  AccountFilterInput,
  AccountWithPermissionFilterInput,
  ChangePermissionDto,
  PaymentType,
  CardFilterInput,
  GenerateQrInput,
  AccountStatus,
  CardStatusesFromPc,
  SetLimitInput,
  SetInternetTransactionInput,
  ChangeCardPermissionDto,
  NaturalPersonCardAccountInput,
  SalePointAccount,
  LegalPartyAndOperationId,
  ConvertAmountRequest,
  AccountType,
} from '@/app/graphql/account/account';

import {
  filterAccountsWithPermissionByType,
  filterAccountsByType,
  formatAccount,
  formatAccountsWithCurrency,
  AccountFormatter,
  mapAccount,
  mapCardBalance,
  mapCardListOperation,
  mapCard,
  mapCardByAccountId,
  mapCardLimit,
  formatAccounts,
  mapAccountForQr,
  flatAccount,
  mapAccountsToBaseRkoAndCardInfo,
} from './helps';
import {
  AccountForPayment,
  Currencies,
  GeneratePersonalQrInput,
  GeneratePersonalQrResponse,
} from '@/shared/services/account/type';
import { personalQrApi } from '@/app/axios';
import { getDeviceIdHeaders } from '@/shared/services/auth/helps';

export async function findAllAccountsByPartyIdWithAcl(filterInput: AccountFilterInput) {
  const response = await sdkAccountService.findAllAccountsByPartyIdWithAcl({ filterInput });
  return filterAccountsByType(response.findAllAccountsByPartyIdWithAcl as AccountForPayment[]);
}

export async function findAllAccountsByPartyIdWithAclMain(filterInput: AccountFilterInput) {
  const response = await sdkAccountService.findAllAccountsByPartyIdWithAclMain({ filterInput });
  return response.findAllAccountsByPartyIdWithAcl;
}

export async function findAllAccountsByPartyIdWithAclId(filterInput: AccountFilterInput) {
  const response = await sdkAccountService.findAllAccountsByPartyIdWithAclId({ filterInput });
  return response.findAllAccountsByPartyIdWithAcl.map((item) => item.id);
}

export async function findAllAccountsByFilterWithPermissions(
  filterInput: AccountFilterInput,
  permissions: PaymentType[]
) {
  const response = await sdkAccountService.findAllAccountsByFilterWithPermissions({ filterInput, permissions });
  return mapAccount(response.findAllAccountsByFilterWithPermissions);
}

export async function findAllAccountsByPartyIdWithPermissionsTypesByBalance(
  filterInput: AccountFilterInput,
  permissions: PaymentType[]
) {
  const accounts = await findAllAccountsByFilterWithPermissions(filterInput, permissions);
  return new AccountFormatter(accounts).sortByBalance().disableByBalance().format();
}

export async function findNationalAccountsByPartyIdWithPermissions(
  filterInput: AccountFilterInput,
  permissions: PaymentType[]
) {
  const accounts = await findAllAccountsByPartyIdWithPermissionsTypesByBalance(filterInput, permissions);
  return new AccountFormatter(accounts).filterByCurrencyKgs().format();
}

export async function findAccountsForQrPayment(orgReferenceId: number) {
  const filterInput = {
    partyId: orgReferenceId,
    statuses: [AccountStatus.Open],
    accountTypes: [AccountType.Rko, AccountType.Card],
  };
  const permissions = [PaymentType.Qr];
  const accounts = await findAllAccountsByFilterWithPermissions(filterInput, permissions);
  const formattedAccounts = new AccountFormatter(accounts)
    .filterByCurrencyKgs()
    .sortByBalance()
    .sortByAccountType()
    .format();

  return mapAccountsToBaseRkoAndCardInfo(formattedAccounts);
}

export async function getAccessControlListForUser(filter: AccountWithPermissionFilterInput) {
  const response = await sdkAccountService.getAccessControlListForUser({ filter });
  return filterAccountsWithPermissionByType(mapAccount(response.getAccessControlListForUser));
}

export async function getAccessControlListForUserCard(filter: AccountWithPermissionFilterInput) {
  const response = await sdkAccountService.getAccessControlListForUserCard({ filter });
  return response.getAccessControlListForUser.filter((item) => item.cards?.length);
}

export async function updatePermissions(changePermission: ChangePermissionDto) {
  const response = await sdkAccountService.updatePermissions({ changePermission });
  return response.updatePermissions;
}

export async function updatePermissionsCard(changePermission: ChangeCardPermissionDto) {
  const response = await sdkAccountService.updatePermissionsCard({ changePermission });
  return response.updatePermissionsCard;
}

export async function getAccountById(accountId: number) {
  const response = await sdkAccountService.getAccountById({ accountId });
  return formatAccount(response.getAccountById);
}

export async function findByAccountAndCurrency(account: string, currencyNum: string) {
  const response = await sdkAccountService.findByAccountAndCurrency({ account, currencyNum });
  return response.findByAccountAndCurrency;
}

export async function findAllByAccount(account: string) {
  const response = await sdkAccountService.findAllByAccount({ account });
  return response.findAllByAccount;
}

export async function findAllByPartyIdBaseRkoAccountInfo(partyId: number) {
  const response = await sdkAccountService.findAllByPartyIdBaseRkoAccountInfo({ partyId });
  return response.findAllByPartyIdBaseRkoAccountInfo;
}

export async function findAccountsWithCurrencyWithPermission(
  filterInput: AccountFilterInput,
  permissions: PaymentType[]
) {
  const accounts = await findAllAccountsByFilterWithPermissions(filterInput, permissions);
  return formatAccountsWithCurrency(accounts);
}

export async function findAllByFilter(filterInput: AccountFilterInput) {
  const accounts = await sdkAccountService.findAllByFilter({ filterInput });
  return formatAccounts(accounts.findAllByFilter);
}

export async function updateVisible(accountId: number, isVisible: boolean) {
  const accounts = await sdkAccountService.updateVisible({ accountId, isVisible });
  return accounts.updateVisible;
}
export async function getAccountsWithPermissionsAndBase(
  filterInput: AccountFilterInput,
  permissions: PaymentType[],
  partyId: number,
  isConversion?: boolean
) {
  const response = await sdkAccountService.getAccountsWithPermissionsAndBase({ filterInput, permissions, partyId });

  return {
    recipientAccounts: response.findAllByPartyIdBaseRkoAccountInfo,
    payerAccounts: new AccountFormatter(mapAccount(response.findAllAccountsByFilterWithPermissions))
      .sortByBalance()
      .disableByBalance(isConversion)
      .format(),
  };
}

export async function getSwiftAccounts(
  filterInput: AccountFilterInput,
  accountPermissions: PaymentType[],
  commissionAccountPermissions: PaymentType[]
) {
  const response = await sdkAccountService.getSwiftAccounts({
    filterInput,
    accountPermissions,
    commissionAccountPermissions,
  });

  return {
    accounts: new AccountFormatter(mapAccount(response.accounts)).sortByBalance().disableByBalance().format(),
    commissionAccounts: new AccountFormatter(mapAccount(response.commissionAccounts))
      .sortByBalance()
      .disableByBalance()
      .format(),
  };
}

export async function getCardTransferAccounts(accountFilterInput: AccountFilterInput, permissions: PaymentType[]) {
  const response = await findAllAccountsByFilterWithPermissions(accountFilterInput, permissions);
  return new AccountFormatter(response)
    .sortByBalance()
    .disableByBalance()
    .format()
    .filter((account) =>
      [Currencies.KGS, Currencies.RUB, Currencies.EUR, Currencies.USD].includes(account.currency.isoCode as Currencies)
    );
}

export async function getCardTransferToBusiness(
  filterInput: AccountFilterInput,
  permissions: PaymentType[],
  cardFilterInput: CardFilterInput
) {
  const response = await sdkAccountService.getCardTransferToBusiness({ filterInput, permissions, cardFilterInput });

  return {
    cards: mapCard(response.findAllCardsByFilter),
    payerAccounts: new AccountFormatter(mapAccount(response.findAllAccountsByFilterWithPermissions))
      .sortByBalance()
      .disableByBalance()
      .format()
      .filter((account) =>
        [Currencies.KGS, Currencies.RUB, Currencies.EUR, Currencies.USD].includes(
          account.currency.isoCode as Currencies
        )
      ),
  };
}

export async function getCardTransferToRko(cardFilterInput: CardFilterInput, partyId: number) {
  const response = await sdkAccountService.getCardTransferToRKo({ cardFilterInput, partyId });

  return {
    cards: mapCard(response.findAllCardsByFilter),
    recipientAccounts: response.findAllByPartyIdBaseRkoAccountInfo.filter((account) =>
      [Currencies.KGS, Currencies.RUB, Currencies.EUR, Currencies.USD].includes(account.currency.isoCode as Currencies)
    ),
  };
}

export async function getAvailableStatusesForCard(cardId: number) {
  const response = await sdkAccountService.getAvailableStatusesForCard({ cardId });
  return response.getAvailableStatusesForCard;
}
export async function getInternetTransactionsAvailability(cardId: number) {
  const response = await sdkAccountService.getInternetTransactionsAvailability({ cardId });
  return response.getInternetTransactionsAvailability;
}

export async function generateElQr(generateQrInput: GenerateQrInput) {
  const response = await sdkAccountService.generateElQr({ generateQrInput });
  return response.generateElQr.elQrUrl;
}

export async function generatePersonalQr(input: GeneratePersonalQrInput): Promise<string | undefined> {
  const response = await personalQrApi().post<GeneratePersonalQrResponse>('generate/qr', input, getDeviceIdHeaders());
  return response.data.qrUrl;
}

export async function findAllCardsByFilterForQr(filterInput: AccountFilterInput, permissions: PaymentType[]) {
  const response = await sdkAccountService.findAllAccountByFilterForQr({
    filterInput,
    permissions,
  });
  return mapAccountForQr(response.findAllAccountsByFilterWithPermissions);
}
//карты виза бизнес
export async function getCardDetailByAccountId(accountId: number, cardStatuses: AccountStatus[]) {
  const response = await sdkAccountService.findAllCardsDetailByAccountId({ accountId, cardStatuses });
  return mapCardBalance(response.findAllCardsByAccountId);
}

export async function findAllCardsByAccountId(accountId: number, cardStatuses: AccountStatus[]) {
  const response = await sdkAccountService.findAllCardsByAccountId({ accountId, cardStatuses });
  return mapCardByAccountId(response.findAllCardsByAccountId);
}

export async function getOperationList(accountId: number, startDate: string, endDate: string) {
  const response = await sdkAccountService.getOperationList({ accountId, startDate, endDate });
  return mapCardListOperation(response.getOperationList);
}

export async function findAllCardsByFilter(filterInput: CardFilterInput) {
  const response = await sdkAccountService.findAllCardsByFilter({ filterInput });
  return mapCard(response.findAllCardsByFilter);
}

export async function findAllCardsByFilterForQrPayment(filterInput: CardFilterInput) {
  const response = await findAllCardsByFilter(filterInput);
  return response.filter((card) => ['KGS', 'ALL'].includes(card.balance[0]?.currency));
}

export async function setCardStatus(cardId: number, status: CardStatusesFromPc, reason: string) {
  const response = await sdkAccountService.setCardStatus({ cardId, status, reason });
  return response.setCardStatus;
}

export async function getCardLimitInfo(cardId: number) {
  const response = await sdkAccountService.getCardLimitInfo({ cardId });
  return mapCardLimit(response.getCardLimitInfo);
}

export async function setInternetTransactionsAvailability(cardId: number, isActive: boolean) {
  const setInternetTransactionInput: SetInternetTransactionInput = {
    isActive,
    reason: 'card_block',
  };

  const response = await sdkAccountService.setInternetTransactionsAvailability({ cardId, setInternetTransactionInput });
  return response.setInternetTransactionsAvailability;
}

export async function setCardLimit(cardId: number, setSetLimitInput: SetLimitInput) {
  const response = await sdkAccountService.setCardLimit({ cardId, setSetLimitInput });
  return response.setCardLimit;
}

export async function unlockCard(cardId: number, reason: string) {
  const response = await sdkAccountService.unlockCard({ cardId, reason });
  return response.unlockCard;
}

export async function updateCardTitle(cardId: number, cardTitle: string) {
  const response = await sdkAccountService.updateCardTitle({ cardId, cardTitle });
  return response.updateCardTitle;
}

export async function getNaturalPersonByPhoneOrCard(input: NaturalPersonCardAccountInput) {
  const response = await sdkAccountService.getNaturalPersonByPhoneOrCard({ naturalPersonCardAccountInput: input });
  return response.getNaturalPersonCards;
}

export async function findAllAccountsByPartyIdWithAclFlatAccount(filterInput: AccountFilterInput) {
  const response = await findAllAccountsByPartyIdWithAcl(filterInput);
  return flatAccount(response);
}

export async function getAllAccountsForSalePoint(legalPartyId: number) {
  const response = await sdkAccountService.getAllAccountsForSalePoint({ legalPartyId });
  return response.getAllAccountsForSalePoint;
}

export async function getAccountBySalePointCode(salePointQr: SalePointAccount) {
  const response = await sdkAccountService.getAccountBySalePointCode({ salePointQr });
  return response.getAccountBySalePointCode;
}

export async function getBalanceInfoByPeriod(accountId: number, startDate: string, endDate: string) {
  const response = await sdkAccountService.getBalanceInfoByPeriod({ accountId, startDate, endDate });
  return response.getBalanceInfoByPeriod;
}

export async function getNptByLegalPartyId(legalPartyId: number) {
  const response = await sdkAccountService.getNptByLegalPartyId({ legalPartyId });
  return response.getNptByLegalPartyId;
}

export async function getRefundedQrOperationInfo(baseOperationId: string, legalPartyId: number) {
  const response = await sdkAccountService.getRefundedQrOperationInfo({ baseOperationId, legalPartyId });
  return response.getRefundedQrOperationInfo;
}

export async function checkRefundIsAvailableFromO24(params: LegalPartyAndOperationId) {
  const response = await sdkAccountService.checkRefundIsAvailableFromO24({ params });
  return response.checkRefundIsAvailableFromO24;
}

export async function convertAmount(convertAmountRequest: ConvertAmountRequest) {
  const response = await sdkAccountService.convertAmount({ convertAmountRequest });
  return response.convertAmount;
}

export async function getAccountReq(accountId: number) {
  const response = await sdkAccountService.getAccountReqById({ accountId });
  return response.getAccountById;
}

export async function getCardAccountReq(accountId: number, cardStatuses: AccountStatus[]) {
  const response = await sdkAccountService.findAllCardReqsByAccountId({ accountId, cardStatuses });
  return response.findAllCardsByAccountId.account;
}

export async function getRequisitesByCurrency(currencyNumber: string, departmentCode: number, accountId: number) {
  const response = await sdkAccountService.getRequisitesByCurrency({ currencyNumber, departmentCode, accountId });
  return response.getRequisitesByCurrency;
}

export async function getCardAccountsFromEfr(partyId: number, legalPartyId: number) {
  const response = await sdkAccountService.getCardAccountsFromEfr({
    getCardAccountFromEfr: { partyId, departmentCode: null },
    legalPartyId,
  });
  return response.getCardAccountsFromEfr;
}
